import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useMutation } from '@apollo/client';
import i18next from 'i18next';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { getFullBackendUrl } from 'helper/download';
import { HotelDocumentOutput } from '__generated__/graphql';
import { UPLOAD_HOTEL_DOCUMENT_MUTATION, DELETE_HOTEL_DOCUMENT_MUTATION, APPROVE_HOTEL_DOCUMENT_MUTATION } from '../pages/settings/gql';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_DOCS = 10;

interface HotelStandardDocumentsProps {
  hotelId: number;
  documents: HotelDocumentOutput[];
  canEdit: boolean;
  isSeminargo: boolean;
  refetch: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const HotelStandardDocuments: React.FC<HotelStandardDocumentsProps> = ({ hotelId, documents, canEdit, isSeminargo, refetch }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [uploading, setUploading] = useState(false);

  const [approveDialogDoc, setApproveDialogDoc] = useState<HotelDocumentOutput | null>(null);

  const [uploadDocument] = useMutation(UPLOAD_HOTEL_DOCUMENT_MUTATION, {
    onCompleted: () => {
      setUploading(false);
      setDisplayName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      dispatchMessage(dispatch, String(i18next.t('hotel-document-upload-success')));
      refetch();
    },
    onError: err => {
      setUploading(false);
      dispatchException(dispatch, err);
    },
  });

  const [deleteDocument] = useMutation(DELETE_HOTEL_DOCUMENT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('hotel-document-delete-success')));
      refetch();
    },
    onError: err => dispatchException(dispatch, err),
  });

  const [approveDocument] = useMutation(APPROVE_HOTEL_DOCUMENT_MUTATION, {
    onCompleted: () => {
      setApproveDialogDoc(null);
      dispatchMessage(dispatch, String(i18next.t('hotel-document-approved-success')));
      refetch();
    },
    onError: err => {
      setApproveDialogDoc(null);
      dispatchException(dispatch, err);
    },
  });

  const handleFileSelect = (file: File | undefined) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      dispatchException(dispatch, new Error(String(i18next.t('hotel-document-pdf-required'))));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      dispatchException(dispatch, new Error(String(i18next.t('hotel-document-max-size'))));
      return;
    }
    if (documents.length >= MAX_DOCS) {
      dispatchException(dispatch, new Error(String(i18next.t('hotel-document-max-count'))));
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      const base64Data = (e.target?.result as string).split(',')[1];
      setUploading(true);
      uploadDocument({
        variables: {
          data: {
            hotelId,
            originalFilename: file.name,
            base64Data,
            mimeType: 'application/pdf',
            displayName: displayName || null,
          },
        },
      });
    };
    reader.onerror = () => {
      dispatchException(dispatch, new Error(String(i18next.t('hotel-document-upload-failed'))));
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = (docId: number) => {
    deleteDocument({ variables: { id: docId } });
  };

  const handleApprove = (docId: number) => {
    approveDocument({ variables: { id: docId } });
  };

  const handleDownload = (fileId: number) => {
    window.open(getFullBackendUrl(`/backend/spacefiles/download/${fileId}`), '_blank');
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={1}>
        <AttachFileIcon sx={{ mr: 1 }} />
        <Typography variant="h6">{i18next.t('hotel-document-title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {i18next.t('hotel-document-subtitle')}
      </Typography>

      {canEdit && (
        <>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                size="small"
                fullWidth
                label={i18next.t('hotel-document-displayname-placeholder')}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                disabled={uploading}
              />
            </Grid>
            <Grid item xs="auto">
              <input
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={e => handleFileSelect(e.target.files?.[0])}
              />
              <Button variant="outlined" startIcon={<UploadIcon />} disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                {uploading ? i18next.t('hotel-settings-agb-uploading') : i18next.t('hotel-settings-agb-upload-select-file')}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {i18next.t('hotel-document-upload-hint')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {documents.length === 0 ? (
        <Alert severity="info">{i18next.t('hotel-document-empty-info')}</Alert>
      ) : (
        <List disablePadding>
          {documents.map(doc => {
            const label = doc.displayName || doc.documentFile.originalFilename;
            const isPending = !doc.approved;
            return (
              <ListItem key={doc.id} divider sx={{ pr: 16 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {isPending ? (
                        <Tooltip title={String(i18next.t('hotel-document-pending-tooltip'))}>
                          <HourglassEmptyIcon
                            fontSize="small"
                            color="warning"
                            sx={{ cursor: isSeminargo ? 'pointer' : 'default' }}
                            onClick={() => isSeminargo && setApproveDialogDoc(doc)}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={String(i18next.t('hotel-document-approved-tooltip'))}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </Tooltip>
                      )}
                      <Typography variant="body2">{label}</Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {doc.documentFile.originalFilename} • {formatFileSize(doc.documentFile.fileSize)} •{' '}
                      {new Date(doc.createdAt).toLocaleDateString(i18next.language)}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleDownload(doc.documentFile.id)} sx={{ mr: 0.5 }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {canEdit && (
                    <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      <Dialog open={!!approveDialogDoc} onClose={() => setApproveDialogDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18next.t('hotel-document-approve-title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18next.t('hotel-document-approve-question', {
              title: approveDialogDoc?.displayName || approveDialogDoc?.documentFile?.originalFilename || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogDoc(null)}>{i18next.t('hotel-document-cancel')}</Button>
          <Button
            color="error"
            onClick={() => {
              if (approveDialogDoc) handleDelete(approveDialogDoc.id);
              setApproveDialogDoc(null);
            }}
          >
            {i18next.t('hotel-document-delete')}
          </Button>
          <Button variant="contained" color="success" onClick={() => approveDialogDoc && handleApprove(approveDialogDoc.id)}>
            {i18next.t('hotel-document-approve')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
